import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Download, Upload, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { importAccounts, type DeletedImportStrategy } from "../../api/accounts";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import type { Account } from "../../lib/types";
import { cn } from "../../lib/utils";

type ImportResult = {
  message: string;
  created: Account[];
  updated: Account[];
  skipped: Array<{ line: number; id: number; current_email: string; reason: string }>;
  errors: Array<{ line: number; message: string }>;
  batch_no: string;
};

type ImportField =
  | "skip"
  | "mj_id"
  | "current_email"
  | "password"
  | "current_email_password"
  | "token"
  | "mfa_secret"
  | "new_email"
  | "old_email"
  | "old_email_password"
  | "remark";

type ParsedLine = {
  line: number;
  raw: string;
  cells: string[];
};

type PreviewRow = {
  line: number;
  raw: string;
  record: Record<string, unknown>;
  errors: string[];
};

const steps = ["粘贴文本", "字段映射", "预览确认"] as const;

const defaultMapping: ImportField[] = ["current_email", "password", "current_email_password", "token", "mfa_secret"];

const fieldOptions: Array<{ value: ImportField; label: string; required?: boolean }> = [
  { value: "skip", label: "跳过" },
  { value: "current_email", label: "当前邮箱", required: true },
  { value: "password", label: "密码", required: true },
  { value: "current_email_password", label: "当前邮箱密码" },
  { value: "token", label: "Discord Token" },
  { value: "mfa_secret", label: "2FA 密钥" },
  { value: "mj_id", label: "MJ ID" },
  { value: "new_email", label: "新邮箱" },
  { value: "old_email", label: "旧邮箱" },
  { value: "old_email_password", label: "旧邮箱密码" },
  { value: "remark", label: "备注" }
];

function splitImportLine(line: string): string[] {
  if (line.includes("——") || line.includes("----") || line.includes("\t")) {
    return line.split(/(?:——|—{2,}|----|\t)/).map((part) => part.trim());
  }
  return line.split(/\s+/).map((part) => part.trim());
}

function parseLines(text: string): ParsedLine[] {
  return text
    .split(/\r?\n/)
    .map((raw, index) => ({ line: index + 1, raw, cells: splitImportLine(raw.trim()) }))
    .filter((row) => row.raw.trim());
}

function mappingForColumn(mapping: ImportField[], index: number): ImportField {
  return mapping[index] ?? "skip";
}

function normalizeMfa(value: string): string {
  return value.replace(/\s+/g, "");
}

function buildRecord(row: ParsedLine, mapping: ImportField[]): Record<string, unknown> {
  const record: Record<string, unknown> = {};
  row.cells.forEach((cell, index) => {
    const field = mappingForColumn(mapping, index);
    if (field === "skip") return;
    record[field] = field === "mfa_secret" ? normalizeMfa(cell) : cell;
  });
  if (!record.original_email && record.current_email) record.original_email = record.current_email;
  if (record.token) record.token_updated_at = new Date().toISOString();
  return record;
}

function previewRows(rows: ParsedLine[], mapping: ImportField[]): PreviewRow[] {
  return rows.map((row) => {
    const record = buildRecord(row, mapping);
    const errors: string[] = [];
    if (!String(record.current_email || "").trim()) errors.push("当前邮箱必填");
    if (!String(record.password || "").trim()) errors.push("密码必填");
    return { line: row.line, raw: row.raw, record, errors };
  });
}

function ensureMappingSize(mapping: ImportField[], size: number): ImportField[] {
  return Array.from({ length: size }, (_, index) => mapping[index] ?? defaultMapping[index] ?? "skip");
}

function cellPreview(rows: ParsedLine[], column: number): string {
  const values = rows
    .slice(0, 4)
    .map((row) => row.cells[column])
    .filter(Boolean);
  return values.length ? values.join(" / ") : "-";
}

export function ImportAccountsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [text, setText] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [batchRemark, setBatchRemark] = useState("");
  const [deletedStrategy, setDeletedStrategy] = useState<DeletedImportStrategy>("keep_deleted");
  const [mapping, setMapping] = useState<ImportField[]>(defaultMapping);
  const [result, setResult] = useState<ImportResult | null>(null);

  const rows = useMemo(() => parseLines(text), [text]);
  const maxColumns = useMemo(() => Math.max(defaultMapping.length, ...rows.map((row) => row.cells.length), 0), [rows]);
  const effectiveMapping = useMemo(() => ensureMappingSize(mapping, maxColumns), [mapping, maxColumns]);
  const previews = useMemo(() => previewRows(rows, effectiveMapping), [effectiveMapping, rows]);
  const validRows = previews.filter((row) => row.errors.length === 0);
  const invalidRows = previews.filter((row) => row.errors.length > 0);
  const duplicateMappedFields = effectiveMapping.filter((field, index) => field !== "skip" && effectiveMapping.indexOf(field) !== index);

  useEffect(() => {
    if (open) {
      setStep(0);
      setResult(null);
    }
  }, [open]);

  useEffect(() => {
    setMapping((current) => ensureMappingSize(current, maxColumns));
  }, [maxColumns]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (duplicateMappedFields.length) throw new Error(`字段不能重复映射：${duplicateMappedFields.join(", ")}`);
      if (!validRows.length) throw new Error("没有可导入的有效行");
      const backendResult = await importAccounts({
        records: validRows.map((row) => row.record),
        batch_no: batchNo,
        batch_remark: batchRemark,
        deleted_strategy: deletedStrategy
      });
      const remappedErrors = backendResult.errors.map((error) => ({
        ...error,
        line: validRows[error.line - 1]?.line ?? error.line
      }));
      const remappedSkipped = backendResult.skipped.map((item) => ({
        ...item,
        line: validRows[item.line - 1]?.line ?? item.line
      }));
      return { ...backendResult, errors: remappedErrors, skipped: remappedSkipped };
    },
    onSuccess: (result) => {
      setResult(result);
      toast.success(result.message);
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
      void queryClient.invalidateQueries({ queryKey: ["batches"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "导入失败")
  });

  if (!open) return null;

  function close() {
    setResult(null);
    onClose();
  }

  function nextStep() {
    if (step === 0 && !rows.length) {
      toast.error("请先粘贴账号文本");
      return;
    }
    if (step === 1 && duplicateMappedFields.length) {
      toast.error(`字段不能重复映射：${duplicateMappedFields.join(", ")}`);
      return;
    }
    setStep((current) => Math.min(current + 1, steps.length - 1));
  }

  function downloadErrors() {
    const combined = [
      ...invalidRows.map((row) => ({ line: row.line, message: row.errors.join("；") })),
      ...(result?.errors ?? []),
      ...(result?.skipped ?? []).map((item) => ({ line: item.line, message: `${item.reason}：${item.current_email || item.id}` }))
    ];
    if (!combined.length) return;
    const body = combined.map((error) => `第 ${error.line} 行\t${error.message}`).join("\n");
    const url = URL.createObjectURL(new Blob([body], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `import-errors-${result?.batch_no || batchNo || "accounts"}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-3">
      <div className="grid max-h-[92vh] w-full max-w-5xl grid-rows-[auto_1fr_auto] rounded-lg bg-white shadow-xl">
        <div className="flex h-12 items-center justify-between border-b px-4">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold">导入账号</h2>
            <div className="hidden items-center gap-1 sm:flex">
              {steps.map((item, index) => (
                <Badge key={item} className={cn(index === step ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600")}>
                  {index + 1}. {item}
                </Badge>
              ))}
            </div>
          </div>
          <Button onClick={close}>
            <X size={14} />
          </Button>
        </div>

        <div className="min-h-0 space-y-3 overflow-auto p-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_220px]">
            <label className="grid gap-1 text-xs">
              批次备注
              <Input value={batchRemark} onChange={(event) => setBatchRemark(event.target.value)} placeholder="例如：0603 Discord 登录补录" />
            </label>
            <label className="grid gap-1 text-xs">
              批次号
              <Input value={batchNo} onChange={(event) => setBatchNo(event.target.value)} placeholder="留空自动生成" />
            </label>
            <label className="grid gap-1 text-xs">
              已删除账号策略
              <select
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs outline-none focus:border-slate-600"
                value={deletedStrategy}
                onChange={(event) => setDeletedStrategy(event.target.value as DeletedImportStrategy)}
              >
                <option value="keep_deleted">保持删除，仅更新</option>
                <option value="restore">自动恢复并更新</option>
                <option value="skip">跳过已删除</option>
              </select>
            </label>
            <div className="grid grid-cols-3 gap-2 text-xs md:col-span-3">
              <div className="rounded-md border bg-slate-50 p-2">总行 <b>{rows.length}</b></div>
              <div className="rounded-md border bg-emerald-50 p-2 text-emerald-700">有效 <b>{validRows.length}</b></div>
              <div className="rounded-md border bg-amber-50 p-2 text-amber-700">异常 <b>{invalidRows.length}</b></div>
            </div>
          </div>

          {step === 0 ? (
            <section className="grid gap-2">
              <label className="grid gap-1 text-xs">
                粘贴文本
                <Textarea
                  rows={16}
                  value={text}
                  onChange={(event) => {
                    setText(event.target.value);
                    setResult(null);
                  }}
                  placeholder="当前邮箱——密码——邮箱密码——token——2FA"
                  className="font-mono leading-5"
                />
              </label>
              <div className="rounded-md bg-slate-100 p-3 text-xs leading-5 text-slate-600">
                支持 `——`、多个横线、Tab 或空白分隔。下一步可以把每一列映射到账号字段，不再要求原始文本完全按固定格式。
              </div>
            </section>
          ) : null}

          {step === 1 ? (
            <section className="overflow-hidden rounded-md border">
              <div className="grid grid-cols-[80px_190px_minmax(0,1fr)] bg-slate-50 text-xs font-semibold text-slate-500">
                <div className="border-b p-2">列</div>
                <div className="border-b p-2">映射字段</div>
                <div className="border-b p-2">样例值</div>
              </div>
              <div className="max-h-[52vh] overflow-auto text-xs">
                {Array.from({ length: maxColumns }).map((_, index) => (
                  <div key={index} className="grid grid-cols-[80px_190px_minmax(0,1fr)] border-b last:border-b-0">
                    <div className="p-2 text-slate-500">第 {index + 1} 列</div>
                    <div className="p-2">
                      <select
                        className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-xs"
                        value={effectiveMapping[index]}
                        onChange={(event) => {
                          const next = [...effectiveMapping];
                          next[index] = event.target.value as ImportField;
                          setMapping(next);
                          setResult(null);
                        }}
                      >
                        {fieldOptions.map((field) => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                            {field.required ? " *" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="break-words p-2 text-slate-600 [overflow-wrap:anywhere]">{cellPreview(rows, index)}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 border-t bg-slate-50 p-2 text-xs">
                {duplicateMappedFields.length ? (
                  <Badge className="bg-rose-50 text-rose-700">
                    <AlertTriangle size={12} />
                    重复字段：{duplicateMappedFields.join(", ")}
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-50 text-emerald-700">
                    <CheckCircle2 size={12} />
                    映射可用
                  </Badge>
                )}
                <Badge className="bg-slate-100 text-slate-700">必填：当前邮箱、密码</Badge>
              </div>
            </section>
          ) : null}

          {step === 2 ? (
            <section className="grid gap-3">
              <div className="grid gap-2 md:grid-cols-4">
                <div className="rounded-md border bg-slate-50 p-3 text-xs">批次 <b>{result?.batch_no || batchNo || "自动生成"}</b></div>
                <div className="rounded-md border bg-emerald-50 p-3 text-xs text-emerald-700">待导入 <b>{validRows.length}</b></div>
                <div className="rounded-md border bg-amber-50 p-3 text-xs text-amber-700">预检异常 <b>{invalidRows.length}</b></div>
                <div className="rounded-md border bg-slate-50 p-3 text-xs">后端错误 / 跳过 <b>{result ? `${result.errors.length} / ${result.skipped.length}` : "0 / 0"}</b></div>
              </div>

              <div className="max-h-[42vh] overflow-auto rounded-md border bg-white text-xs">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
                    <tr>
                      <th className="border-b p-2">行</th>
                      <th className="border-b p-2">当前邮箱</th>
                      <th className="border-b p-2">密码</th>
                      <th className="border-b p-2">Token</th>
                      <th className="border-b p-2">2FA</th>
                      <th className="border-b p-2">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previews.slice(0, 200).map((row) => (
                      <tr key={row.line} className={row.errors.length ? "bg-amber-50" : ""}>
                        <td className="border-b p-2">{row.line}</td>
                        <td className="border-b p-2">{String(row.record.current_email || "-")}</td>
                        <td className="border-b p-2">{String(row.record.password || "").trim() ? "已填" : "-"}</td>
                        <td className="border-b p-2">{String(row.record.token || "").trim() ? "已填" : "-"}</td>
                        <td className="border-b p-2">{String(row.record.mfa_secret || "").trim() ? "已填" : "-"}</td>
                        <td className="border-b p-2">
                          {row.errors.length ? <span className="text-amber-700">{row.errors.join("；")}</span> : <span className="text-emerald-700">可导入</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previews.length > 200 ? <div className="p-2 text-slate-500">仅展示前 200 行，实际会导入全部有效行。</div> : null}
              </div>

              {result ? (
                <div className="grid gap-3 rounded-md border bg-slate-50 p-3 text-xs">
                  <div className="grid grid-cols-4 gap-2">
                    <div>批次 <b>{result.batch_no || "-"}</b></div>
                    <div>创建 <b>{result.created.length}</b></div>
                    <div>更新 <b>{result.updated.length}</b></div>
                    <div>跳过 / 失败 <b>{result.skipped.length} / {result.errors.length}</b></div>
                  </div>
                  {result.skipped.length ? (
                    <div className="max-h-32 overflow-auto rounded border bg-white">
                      {result.skipped.map((item) => (
                        <div key={`${item.line}-${item.id}`} className="border-b p-2 last:border-b-0">
                          第 {item.line} 行：{item.reason}（{item.current_email || item.id}）
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {result.errors.length ? (
                    <div className="max-h-32 overflow-auto rounded border bg-white">
                      {result.errors.map((error) => (
                        <div key={`${error.line}-${error.message}`} className="border-b p-2 last:border-b-0">
                          第 {error.line} 行：{error.message}
                        </div>
                      ))}
                    </div>
                  ) : !result.skipped.length ? (
                    <div className="text-slate-500">导入完成，没有后端错误明细。</div>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t p-3">
          <div className="flex gap-2">
            {invalidRows.length || result?.errors.length || result?.skipped.length ? (
              <Button onClick={downloadErrors}>
                <Download size={14} />
                下载错误明细
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button onClick={close}>{result ? "关闭" : "取消"}</Button>
            {step > 0 ? (
              <Button onClick={() => setStep((current) => Math.max(0, current - 1))}>
                <ArrowLeft size={14} />
                上一步
              </Button>
            ) : null}
            {step < steps.length - 1 ? (
              <Button onClick={nextStep}>
                下一步
                <ArrowRight size={14} />
              </Button>
            ) : (
              <Button disabled={!validRows.length || mutation.isPending} onClick={() => mutation.mutate()} className="bg-slate-900 text-white hover:bg-slate-800">
                <Upload size={14} />
                确认导入
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
